import useDecodedParams from "Hooks/useDecodedParams";
import useSelectorWithUrlParams from "Hooks/useSelectorWithUrlParams";
import { selectHasPermissionToManageAutoscaling } from "Reducers/environment";
import { servicesSelector } from "Reducers/environment/service/service.selectors";
import { projectCapabilitySelector } from "Reducers/project/capabilities";
import { useAppSelector } from "Store/hooks";

import { MetricsDeploymentService } from "./type";

const defaultOrder = ["cpu", "memory", "/mnt", "/tmp"];
export const serviceOrder = data => {
  const count = Object.keys(data || {}).filter(k => k);

  const order = count.length ? count : defaultOrder;
  const v = order.reduce(
    (acc, current) => {
      acc.push([current, data?.[current] || []]);
      return acc;
    },
    [] as [string, Array<Record<string, number>>][]
  );
  return v.sort(
    (a, b) => defaultOrder.indexOf(a[0]) - defaultOrder.indexOf(b[0])
  );
};

const getAutoscalingThresholds = ({
  service,
  serviceUnit,
  thresholdsEnabled,
  thresholdUpperValue,
  thresholdLowerValue
}) => {
  const PERCENTAGE_DIVISOR = 100;

  const isAutoscalingEnabled =
    (service?.kind === "webapp" || service?.class === "app") &&
    serviceUnit === "cpu" &&
    thresholdsEnabled;

  if (!isAutoscalingEnabled) return undefined;

  return [
    {
      label: "autoscaling-upper",
      value: thresholdUpperValue / PERCENTAGE_DIVISOR
    },
    {
      label: "autoscaling-lower",
      value: thresholdLowerValue / PERCENTAGE_DIVISOR
    }
  ];
};

export const useAutoscallingValues = (serviceName: string = "") => {
  const autoscalingConfig = useAppSelector(servicesSelector);
  const thresholdsEnabled =
    autoscalingConfig?.autoscalingConfiguration?.services?.[serviceName]
      ?.enabled;

  const thresholdUpperValue =
    autoscalingConfig?.autoscalingConfiguration?.services?.[serviceName]
      ?.triggers?.cpu?.up?.threshold;
  const thresholdLowerValue =
    autoscalingConfig?.autoscalingConfiguration?.services?.[serviceName]
      ?.triggers?.cpu?.down?.threshold;
  const { organizationId, projectId } = useDecodedParams();

  const capabilities = useAppSelector(state =>
    projectCapabilitySelector(state, { projectId, organizationId })
  );

  const hasCapability = capabilities?.autoscaling?.enabled;
  const canManageAutoscaling = useSelectorWithUrlParams(
    selectHasPermissionToManageAutoscaling
  );

  type GetThresholdProps = {
    service?: MetricsDeploymentService;
    serviceUnit: string;
  };

  const getThreshold = ({ service, serviceUnit }: GetThresholdProps) => {
    const thresholds = getAutoscalingThresholds({
      service,
      serviceUnit,
      thresholdsEnabled,
      thresholdUpperValue,
      thresholdLowerValue
    });
    return { thresholds, thresholdsEnabled, hasCapability };
  };

  return {
    thresholdsEnabled,
    thresholdUpperValue,
    thresholdLowerValue,
    hasCapability,
    getThreshold,
    canManageAutoscaling
  };
};
